class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onApplyClick = this.onApplyClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 350);

    const facetForm = this.querySelector('form');
    if (!facetForm) return;

    facetForm.addEventListener('input', this.debouncedOnSubmit);
    facetForm.addEventListener('click', this.onApplyClick, true);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    window.addEventListener('popstate', (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    });
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static setLoading(isLoading) {
    const gridContainer = document.getElementById('ProductGridContainer');
    const collection = gridContainer?.querySelector('.collection');
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    collection?.classList.toggle('loading', isLoading);
    gridContainer?.setAttribute('aria-busy', String(isLoading));
    countContainer?.classList.toggle('loading', isLoading);
    countContainerDesktop?.classList.toggle('loading', isLoading);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    if (!sections.length) return;

    FacetFiltersForm.setLoading(true);

    sections.forEach((section) => {
      const query = searchParams ? `&${searchParams}` : '';
      const url = `${window.location.pathname}?section_id=${section.section}${query}`;
      const cached = FacetFiltersForm.filterData.find((item) => item.url === url);

      if (cached) {
        FacetFiltersForm.renderSection(cached.html, event);
        return;
      }

      FacetFiltersForm.abortController?.abort();
      FacetFiltersForm.abortController = new AbortController();

      fetch(url, {
        signal: FacetFiltersForm.abortController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Filter request failed: ${response.status}`);
          return response.text();
        })
        .then((html) => {
          FacetFiltersForm.filterData = [
            ...FacetFiltersForm.filterData.filter((item) => item.url !== url),
            { html, url },
          ].slice(-20);
          FacetFiltersForm.renderSection(html, event);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.error(error);
          window.location.assign(`${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`);
        });
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSection(html, event) {
    try {
      FacetFiltersForm.renderFilters(html, event);
      FacetFiltersForm.renderProductGridContainer(html);
      FacetFiltersForm.renderProductCount(html);
      document.dispatchEvent(new CustomEvent('whitehole:catalog-updated'));
    } catch (error) {
      console.error('Unable to update the catalog', error);
      FacetFiltersForm.setLoading(false);
    }
  }

  static parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  static renderProductGridContainer(html) {
    const parsed = FacetFiltersForm.parseHTML(html);
    const source = parsed.getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (!source || !target) return;
    target.innerHTML = source.innerHTML;
    target.setAttribute('aria-busy', 'false');
  }

  static renderProductCount(html) {
    const parsed = FacetFiltersForm.parseHTML(html);
    const source = parsed.getElementById('ProductCount');
    if (!source) {
      FacetFiltersForm.setLoading(false);
      return;
    }

    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      const target = document.getElementById(id);
      if (!target) return;
      target.innerHTML = source.innerHTML;
      target.classList.remove('loading');
    });
  }

  static renderFilters(html, event) {
    const parsedHTML = FacetFiltersForm.parseHTML(html);
    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const activeFilter = event?.target?.closest?.('.js-filter');
    const activeIndex = activeFilter?.dataset?.index;

    facetDetailsElements.forEach((element) => {
      if (activeIndex && element.dataset.index === activeIndex) return;
      const target = document.querySelector(`.js-filter[data-index="${CSS.escape(element.dataset.index || '')}"]`);
      if (target) target.innerHTML = element.innerHTML;
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (activeIndex) {
      const source = Array.from(facetDetailsElements).find((element) => element.dataset.index === activeIndex);
      if (source && activeFilter) FacetFiltersForm.renderCounts(source, activeFilter);
    }
  }

  static renderActiveFacets(html) {
    ['.active-facets-mobile', '.active-facets-desktop'].forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    ['.mobile-facets__open', '.mobile-facets__count', '.sorting'].forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });

    const drawer = document.getElementById('FacetFiltersFormMobile')?.closest('menu-drawer');
    if (drawer?.bindEvents) drawer.bindEvents();
  }

  static renderCounts(source, target) {
    const sourceSelected = source.querySelector('.facets__selected');
    const targetSelected = target.querySelector('.facets__selected');
    if (sourceSelected && targetSelected) targetSelected.outerHTML = sourceSelected.outerHTML;

    const sourceSummary = source.querySelector('.facets__summary');
    const targetSummary = target.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) targetSummary.outerHTML = sourceSummary.outerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`
    );
  }

  static getSections() {
    const grid = document.getElementById('product-grid');
    return grid?.dataset?.id ? [{ section: grid.dataset.id }] : [];
  }

  createSearchParams(form) {
    return new URLSearchParams(new FormData(form)).toString();
  }

  closeMobileDrawer(event) {
    const form = event?.target?.closest?.('form');
    const drawer = form?.closest?.('menu-drawer');
    const summary = drawer?.querySelector(':scope > details > summary');
    if (drawer?.closeMenuDrawer && summary) {
      drawer.closeMenuDrawer(event || new Event('click'), summary);
    }
  }

  onApplyClick(event) {
    const button = event.target.closest('[data-wh99-apply-filters]');
    if (!button || !this.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();

    const form = button.closest('form');
    if (!form) return;
    this.onSubmitForm(this.createSearchParams(form), event);
    this.closeMobileDrawer(event);
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    const sourceForm = event.target.closest('form');
    if (!sourceForm) return;

    // Mobile selections are collected and applied once with the Apply button.
    // This avoids an AJAX request for every tap and keeps the drawer responsive.
    if (sourceForm.id === 'FacetFiltersFormMobile') return;

    event.preventDefault();
    const forms = [];
    document.querySelectorAll('facet-filters-form form').forEach((form) => {
      if (['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'].includes(form.id)) {
        form.querySelectorAll('.no-js-list').forEach((element) => element.remove());
        forms.push(this.createSearchParams(form));
      }
    });
    this.onSubmitForm(forms.filter(Boolean).join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url = event.currentTarget.href;
    const searchParams = url.includes('?') ? url.slice(url.indexOf('?') + 1) : '';
    FacetFiltersForm.renderPage(searchParams);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.abortController = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
  FacetFiltersForm.setListeners();
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const [minInput, maxInput] = this.querySelectorAll('input');
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (!minInput.value) maxInput.setAttribute('min', '0');
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));
    if (!Number.isFinite(value)) return;
    if (Number.isFinite(min) && value < min) input.value = min;
    if (Number.isFinite(max) && value > max) input.value = max;
  }
}

if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (!facetLink) return;
    facetLink.setAttribute('role', 'button');
    facetLink.classList.add('js-facet-remove');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form?.onActiveFilterClick(event);
  }
}

if (!customElements.get('facet-remove')) customElements.define('facet-remove', FacetRemove);
